import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { GlobalOrderResponse } from '@api/ringil-api.v2';
import IconBlock from '@components/ringil3/IconBlock/IconBlock';
import { GrayText, LargeText } from '@components/ringil3/styles';
import { formatVolume, formatWeight, isNotBlank, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import SvgCargoItem from '@components/ringil3/icons/CargoItem';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import SvgRedInfo from '@components/ringil3/icons/RedInfo.tsx';
import FoldableEanCodes from '@components/ringil3/FoldableEanCodes/FoldableEanCodes.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import { toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  type: 'loading' | 'unloading' | 'neutral';
  order: GlobalOrderResponse;
  showDiscrepancy?: boolean;
  showReturnablePackage?: boolean;
  showVolume?: boolean;
  dataTestIdPrefix?: string;
  orderIndex?: number;
}

const RingilOrder: React.FC<PropsWithChildren<ComponentProps>> = ({
  type = 'neutral',
  order,
  showDiscrepancy = false,
  showReturnablePackage = false,
  showVolume = false,
  dataTestIdPrefix,
  orderIndex,
}) => {
  const { tr } = useTranslationLgs();

  const visuallyHiddenStyle: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  };

  const orderPrefix =
    dataTestIdPrefix != null && orderIndex != null
      ? `${dataTestIdPrefix}-orderReference${orderIndex + 1}`
      : undefined;
  const orderSlugPrefix = dataTestIdPrefix ? `${dataTestIdPrefix}-orderReference-${toTestIdSlug(order.orderName)}` : undefined;
  const orderPrefixes = [orderPrefix, orderSlugPrefix].filter((value): value is string => Boolean(value));
  const orderSlugDetailsPrefixes = orderSlugPrefix ? [orderSlugPrefix] : [];

  const renderHiddenValues = (testIds: string[], value: React.ReactNode) => {
    return testIds.map((testId) => (
      <span key={testId} style={visuallyHiddenStyle} data-testid={testId}>
        {value}
      </span>
    ));
  };

  const calculateOrderWeight = (order: GlobalOrderResponse): number => {
    return order.items.reduce((acc, item) => acc + item.weight, 0);
  };

  return (
    <RingilOrderInner>
      <div className="mb-4">
        <div data-testid={orderSlugPrefix}>
          <COBadge variant="filled" type={type}>
            <span data-testid={orderPrefix ? `${orderPrefix}-id` : undefined}>{order.orderName}</span>
            {renderHiddenValues(orderSlugDetailsPrefixes.map(prefix => `${prefix}-id`), order.orderName)}
            {renderHiddenValues(orderPrefixes.map(prefix => `${prefix}-returnablePackaging`), showReturnablePackage ? 'True' : 'False')}
          </COBadge>
        </div>
      </div>
      {order.items.map((cargo, cargoIndex) => {
        const manUnitPrefixes = orderPrefixes.map(prefix => `${prefix}-manUnit${cargoIndex + 1}`);
        return (
          <React.Fragment key={cargoIndex}>
            <IconBlock icon={<SvgCargoItem />}>
              {renderHiddenValues(manUnitPrefixes, cargo.cargoTemplate?.name ?? '')}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-Amount`), cargo.amount ?? '')}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-stackable`), String(!!cargo.stackable))}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-NoteManUnit`), cargo.itemNote ?? '')}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-Length`), cargo.length ?? '')}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-Width`), cargo.width ?? '')}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-Height`), cargo.height ?? '')}
              {renderHiddenValues(manUnitPrefixes.map(prefix => `${prefix}-Weight`), cargo.weight ?? '')}

              {cargo.stackable ? (
                <LargeText>
                  {tr(`CargoTemplate.${cargo.cargoTemplate.code}`, cargo.cargoTemplate.name)} (<strong>{cargo.amount}</strong>), {tr(`RingilOrder.stackable`, `stackable`)}
                </LargeText>
              ) : (
                <LargeText>
                  {tr(`CargoTemplate.${cargo.cargoTemplate.code}`, cargo.cargoTemplate.name)} (<strong>{cargo.amount}</strong>), {tr(`RingilOrder.nonstackable`, `non-stackable`)}
                </LargeText>
              )}
              {showDiscrepancy && cargo.discrepancyAmount === cargo.amount ? (
                <p className=" text-xs font-bold">
                  {tr(`RingilOrder.cargoTemplateDiscrepancy`, `Reality: {{unit}} ({{count}})`, {
                    unit: cargo.cargoTemplate.name,
                    count: cargo.discrepancyAmount,
                  })}
                </p>
              ) : (
                showDiscrepancy &&
                cargo.discrepancyAmount != null &&
                cargo.discrepancyAmount >= 0 && (
                  <p className="text-red-600 text-xs font-bold">
                    {tr(`RingilOrder.cargoTemplateDiscrepancy`, `Reality: {{unit}} ({{count}})`, {
                      unit: cargo.cargoTemplate.name,
                      count: cargo.discrepancyAmount,
                    })}
                  </p>
                )
              )}
              <GrayText>
                {cargo.weight > 0 && <div>{formatWeight(cargo.weight)}</div>}
                {cargo.length + cargo.height + cargo.width > 0 && (
                  <div>
                    {tr(`RingilOrder.dimensions`, `Dimensions: {{length}} x {{width}} x {{height}} cm (l x w x h)`, {
                      length: cargo.length,
                      width: cargo.width,
                      height: cargo.height,
                    })}
                  </div>
                )}
                {cargo.manipulationUnitInfo && cargo.manipulationUnitInfo.length > 0 && (
                  <div>
                    <span>{tr(`RingilOrder.manipulationUnitInfoLabel`, `Manipulation units:`)}</span>
                    <div className="flex flex-col">
                      {cargo.manipulationUnitInfo
                        .map(mu =>
                          [
                            isNotBlank(mu.externalId) ? mu.externalId : null,
                            isNotBlank(mu.name) ? mu.name : null,
                            mu.count != null ? `${mu.count}` : null,
                            mu.weight != null && mu.weight > 0 ? formatWeight(mu.weight) : null,
                            isNotBlank(mu.size) ? mu.size : null,
                            isNotBlank(mu.wheelbase) ? mu.wheelbase : null,
                          ]
                            .filter(Boolean)
                            .join(', ')
                        )
                        .map(unit => <span>{unit}</span>)}
                    </div>
                  </div>
                )}
                {isNotBlank(cargo.itemNote) && <div>{tr('RingilOrder.itemNote', 'Poznámka: {{itemNote}}', { itemNote: cargo.itemNote })}</div>}
              </GrayText>
            </IconBlock>
          </React.Fragment>
        );
      })}
      <IconBlock icon={<SvgGrayInfo />}>
        <div>
          <div style={visuallyHiddenStyle}>
            {renderHiddenValues(orderPrefixes.map(prefix => `${prefix}-goodsInfo-numProducts`), order.orderAmount ?? '')}
            {renderHiddenValues(orderPrefixes.map(prefix => `${prefix}-goodsInfo-description`), order.orderDescription ?? '')}
            {renderHiddenValues(orderPrefixes.map(prefix => `${prefix}-goodsInfo-valueOfGoods`), order.orderValue?.price ?? '')}
            {renderHiddenValues(orderPrefixes.map(prefix => `${prefix}-goodsInfo-valueOfGoodsCurrency`), order.orderValue?.currency ?? '')}
            {renderHiddenValues(orderPrefixes.map(prefix => `${prefix}-noteToOrder-input`), order.orderNote ?? '')}
          </div>
          {(isNotNullOrUndefined(order.orderAmount) || isNotNullOrUndefined(order.orderDescription)) && (
            <GrayText>
              {`${order.orderDescription ? order.orderDescription : ''}${order.orderDescription && order.orderAmount ? ': ' : ''}${
                order.orderAmount ? tr(`RingilOrder.orderAmount`, `{{orderAmount}} pcs`, { orderAmount: order.orderAmount }) : ''
              }`}
            </GrayText>
          )}
          <GrayText>
            {tr(`RingilOrder.orderWeight`, `Celková hmotnost: {{weight}}`, {
              weight: isNullOrUndefined(order.orderTotalWeight) ? formatWeight(calculateOrderWeight(order)) : formatWeight(order.orderTotalWeight),
            })}
            {showVolume &&
              ` | ${tr(`RingilOrder.orderVolume`, `Celkový objem: {{volume}}`, {
                volume: formatVolume(order.totalVolume),
              })}`}
          </GrayText>
          {showReturnablePackage && (
            <GrayText>
              {tr(`RingilOrder.returnablePackage`, `Returnable package`)}
            </GrayText>
          )}
          {isNotNullOrUndefined(order.orderValue) && isNotNullOrUndefined(order.orderValue?.price) && (
            <GrayText>
              {tr(`RingilOrder.orderValues`, `Hodnota objednávky: {{formattedPrice}}`, {
                formattedPrice: formatPrice(order.orderValue.price, order.orderValue.currency),
              })}
            </GrayText>
          )}

          {isNotBlank(order.orderNote) && (
            <GrayText>
              {tr(`RingilOrder.orderNote`, `Poznamka: {{orderNote}}`, {
                orderNote: order.orderNote,
              })}
            </GrayText>
          )}
        </div>
      </IconBlock>

      {showDiscrepancy && order.discrepancyNote && (
        <IconBlock icon={<SvgRedInfo />}>
          <LargeText>{tr(`RingilOrder.discrepancyNote`, `Discrepancy note`)}</LargeText>
          <p className="text-[0.75rem]">{order.discrepancyNote}</p>
        </IconBlock>
      )}

      <FoldableEanCodes eanCodes={order.loadedEans} />
    </RingilOrderInner>
  );
};

const RingilOrderInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export default RingilOrder;
